import React, { useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import { googleSheetsService } from "../../services/apiService";
import { metaPixel } from "../../services/metaPixel";

type FormState = {
  studentName: string;
  email: string;
  gender: string;
  birthDate: string;
  phoneCode: string;
  phoneNumber: string;
  school: string;
  grade: string;
  city: string;
  studentCategory: string;
  educationSystem: string;
  firstLanguage: string;
  secondLanguage: string;
  lastGrade: string;
};

const initialFormState: FormState = {
  studentName: "",
  email: "",
  gender: "",
  birthDate: "",
  phoneCode: "+20",
  phoneNumber: "",
  school: "",
  grade: "",
  city: "",
  studentCategory: "",
  educationSystem: "",
  firstLanguage: "",
  secondLanguage: "",
  lastGrade: "",
};

const ContactForm: React.FC = () => {
  const { t, dir } = useLanguage();
  const isRtl = dir === "rtl";
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState<FormState>(initialFormState);

  const contact = (t as any)?.contact || {};
  const leadTitle = contact.formLeadTitle || "Tell us about your child.";
  const leadSubtitle =
    contact.formLeadSubtitle ||
    "Fill out the form below and one of our advisors will reach out to you as soon as possible.";

  const fields = contact.formFields || {};

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setError(false);

    try {
      await googleSheetsService.submitEnrollment(formData);

      // Fire the Lead conversion event only now that the backend has
      // confirmed the row was saved — never on click, never on failure.
      metaPixel.trackLead({
        email: formData.email,
        phone: `${formData.phoneCode}${formData.phoneNumber}`,
        city: formData.city,
      });

      setSuccess(true);
      setFormData(initialFormState);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error("Contact form submission failed:", err);
      setError(true);
      setTimeout(() => setError(false), 5000);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const inputClass =
    `h-12 w-full rounded-lg border border-stone-200 bg-stone-100/80 px-4 font-sans text-base font-normal leading-6 text-black outline-none transition focus:border-second-color focus:bg-white focus:ring-2 focus:ring-second-color/15 ${
      isRtl ? "text-right" : "text-left"
    }`;
  const selectClass =
    `${inputClass} appearance-none bg-[linear-gradient(45deg,transparent_50%,#6b7280_50%),linear-gradient(135deg,#6b7280_50%,transparent_50%)] ${
      isRtl
        ? "bg-[position:20px_20px,26px_20px]"
        : "bg-[position:calc(100%-20px)_20px,calc(100%-14px)_20px]"
    } bg-[size:6px_6px,6px_6px] bg-no-repeat`;
  const labelClass =
    `mb-2 block font-sans text-sm font-semibold leading-5 text-teal-900 ${
      isRtl ? "text-right" : "text-left"
    }`;
  const gridClass = "grid gap-5 sm:grid-cols-2";
  const fieldClass = isRtl ? "text-right" : "text-left";
  const firstFieldClass = `${fieldClass} ${isRtl ? "sm:order-2" : "sm:order-1"}`;
  const secondFieldClass = `${fieldClass} ${isRtl ? "sm:order-1" : "sm:order-2"}`;

  return (
    <section className="border-b border-stone-200 bg-white px-4 pt-32 pb-16 sm:px-16 sm:pt-36 sm:pb-20 lg:px-16 lg:pt-40 lg:pb-24">
      <div className="mx-auto flex w-full flex-col gap-8">
        <div
          className={`flex flex-col gap-4 w-full max-w-2xl ${
            isRtl ? "items-end text-right self-end" : "items-start text-left self-start"
          }`}
        >
          <h2 className="font-sans text-2xl font-bold leading-tight text-teal-900 sm:text-[32px] sm:leading-[1.2]">
            {leadTitle}
          </h2>
          <p className="font-sans text-sm font-normal leading-6 text-black sm:text-base">
            {leadSubtitle}
          </p>
        </div>

        {success ? (
          <div
            className="rounded-lg border border-green-300 bg-green-50 p-4 font-sans text-base text-green-700"
            aria-live="polite"
          >
            {contact.form?.success || "Thank you! Your message has been sent successfully."}
          </div>
        ) : null}

        {error ? (
          <div
            className="rounded-lg border border-red-300 bg-red-50 p-4 font-sans text-base text-red-700"
            aria-live="polite"
          >
            {contact.form?.error || "Something went wrong. Please check your connection and try again."}
          </div>
        ) : null}

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-6"
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="studentName" className={labelClass}>
                {fields.studentName || "Student name"}
              </label>
              <input
                id="studentName"
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
                placeholder={fields.studentNamePlaceholder || "Type your Student name"}
                className={inputClass}
                required
              />
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="email" className={labelClass}>
                {fields.email || "Email"}
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={fields.emailPlaceholder || "Type your email"}
                className={inputClass}
                required
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="gender" className={labelClass}>
                {fields.gender || "Gender"}
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={selectClass}
              >
                <option value="">{fields.selectPlaceholder || "Suggestion"}</option>
                <option value="male">{fields.male || "Male"}</option>
                <option value="female">{fields.female || "Female"}</option>
              </select>
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="birthDate" className={labelClass}>
                {fields.birthDate || "Birth date"}
              </label>
              <input
                id="birthDate"
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="phoneNumber" className={labelClass}>
                {fields.phoneNumber || "Phone number"}
              </label>
              <div
                className={`flex h-12 overflow-hidden rounded-lg border border-stone-200 bg-stone-100/80 focus-within:border-second-color focus-within:bg-white focus-within:ring-2 focus-within:ring-second-color/15 ${
                  isRtl ? "flex-row-reverse" : ""
                }`}
              >
                <select
                  name="phoneCode"
                  value={formData.phoneCode}
                  onChange={handleChange}
                  className={`min-w-[92px] bg-stone-200 px-3 font-sans text-base font-medium text-neutral-900 outline-none ${
                    isRtl ? "border-l border-stone-200" : "border-r border-stone-200"
                  }`}
                >
                  <option value="+966">+966</option>
                  <option value="+20">+20</option>
                  <option value="+249">+249</option>
                  <option value="+962">+962</option>
                </select>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder={fields.phoneNumberPlaceholder || "00 000 0000"}
                  className={`h-12 w-full bg-transparent px-4 font-sans text-base font-normal leading-6 text-black outline-none ${isRtl ? "text-right" : "text-left"}`}
                />
              </div>
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="school" className={labelClass}>
                {fields.school || "School"}
              </label>
              <input
                id="school"
                name="school"
                value={formData.school}
                onChange={handleChange}
                placeholder={fields.schoolPlaceholder || "Type your School name"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="grade" className={labelClass}>
                {fields.grade || "Grade"}
              </label>
              <input
                id="grade"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                placeholder={fields.gradePlaceholder || "Type your grade"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="city" className={labelClass}>
                {fields.city || "City"}
              </label>
              <input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={fields.cityPlaceholder || "Type your city"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="studentCategory" className={labelClass}>
                {fields.studentCategory || "Student category"}
              </label>
              <select
                id="studentCategory"
                name="studentCategory"
                value={formData.studentCategory}
                onChange={handleChange}
                className={selectClass}
                required
              >
                <option value="">{fields.selectPlaceholder || "Suggestion"}</option>
                <option value="egyptian">{fields.egyptian || "Egyptian"}</option>
                <option value="international">{fields.international || "International"}</option>
                <option value="non-egyptian">{fields.nonEgyptian || "Non-Egyptian"}</option>
              </select>
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="educationSystem" className={labelClass}>
                {fields.educationSystem || "Education system"}
              </label>
              <input
                id="educationSystem"
                name="educationSystem"
                value={formData.educationSystem}
                onChange={handleChange}
                placeholder={fields.educationSystemPlaceholder || "Type the education system"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={firstFieldClass}>
              <label htmlFor="firstLanguage" className={labelClass}>
                {fields.firstLanguage || "First language"}
              </label>
              <input
                id="firstLanguage"
                name="firstLanguage"
                value={formData.firstLanguage}
                onChange={handleChange}
                placeholder={fields.firstLanguagePlaceholder || "Type the first language"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
            <div className={secondFieldClass}>
              <label htmlFor="secondLanguage" className={labelClass}>
                {fields.secondLanguage || "Second language"}
              </label>
              <input
                id="secondLanguage"
                name="secondLanguage"
                value={formData.secondLanguage}
                onChange={handleChange}
                placeholder={fields.secondLanguagePlaceholder || "Type the second language"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          <div className={gridClass}>
            <div className={`md:max-w-none ${fieldClass} ${isRtl ? "sm:col-start-2" : ""}`}>
              <label htmlFor="lastGrade" className={labelClass}>
                {fields.lastGrade || "Last grade"}
              </label>
              <input
                id="lastGrade"
                name="lastGrade"
                value={formData.lastGrade}
                onChange={handleChange}
                placeholder={fields.lastGradePlaceholder || "Type your last grade"}
                className={`${inputClass} ${isRtl ? "text-right" : "text-left"}`}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`inline-flex min-h-12 w-full items-center justify-center rounded-lg bg-main-color px-6 py-3 font-sans text-base font-medium leading-6 text-second-color transition-colors hover:bg-lime-300 disabled:cursor-not-allowed disabled:opacity-70 sm:w-fit sm:min-w-[160px] ${
              isRtl ? "self-end" : "self-start"
            }`}
          >
            {isSubmitting
              ? contact.form?.sending || "Sending..."
              : fields.submit || "Send My Details"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
